<template>
  <header class="sysbar" :class="{ 'sysbar--narrow': narrow }">
    <div class="sysbar__left">
      <app-hub-tenant class="sysbar__tenant" />
      <app-hub-breadcrumb class="sysbar__crumb" :notch="false" />
    </div>
    <div class="sysbar__right">
      <app-hub-search />
      <app-product-switch />
      <app-messages />
      <app-theme-toggle />
      <app-hub-status compact />
    </div>
  </header>
</template>

<script>
// Systemleiste oben im Blatt der klassischen Shell (Kundenwunsch 12.10.2026): wie die Kopfleiste des Mobility Hub,
// aber innerhalb der abgerundeten Inhaltskarte und ohne Benutzer (der steht unten in der Navigation).
// Links Mandant und Brotkrumen wie im Hub: AppHubTenant und AppHubBreadcrumb „/ Gruppe / Seite“; wie im Beispiel des
// Kunden sind die übergeordneten Ebenen gedämpft (--ink-3, 400), nur die aktuelle Seite steht in --ink 500. Der Mandant
// ist für alle angemeldeten Benutzer ein Auswahlmenü mit Kundensuche (Kundenwunsch 12.10.2026); die Wahl eines anderen
// Mandanten öffnet die Anmeldung beim neuen Mandanten (AppTenantSwitch). Das Gebäude-Icon fluchtet mit dem Seitenrand.
// Rechts die Suche in der Navigation (AppHubSearch, ⌘K bzw. Strg+K), dahinter der Produktwechsel Ticketing ↔
// Kontrolle (Mobility Hub) mit der Version im Menü (AppProductSwitch, Kundenwunsch 13.10.2026: „hinter das suchfeld“),
// der Knopf „Darstellung“ (Farbschema und Hauptfarbe) (AppThemeToggle, Kundenwunsch 12.10.2026: links neben dem
// WLAN-Symbol; Nachtrag 12.10.2026 Nr. 15) und der
// Verbindungsstatus als runder Icon-Knopf (AppHubStatus compact; die Karte zeigt Status, Stand, Version und Mandant);
// alle drei mit Kante und Schatten wie die Knöpfe im Blatt (--edge, --sh-ctl). Fläche --surface (im dunklen
// Farbschema wie das Blatt).
// Höhe var(--sysbar-h) (MainLayout): Polster 12 um die Bedienelemente (--h-ctl 32, Touch 44) plus Linie --line unten,
// also 57 bzw. 69; die Mitte liegt damit auf der Kopfzeile der Navigation (y = 37 bzw. 43, wie Logo und „Seitenleiste
// einklappen“). Seitlich 20 wie der Seitenrand --page-x (Touch unter 960 px 16). Nicht auf dem Handy (dort App-Leiste);
// beim Drucken ausgeblendet. Keine Props, Ereignisse oder Slots.
import AppHubTenant from '../hub/AppHubTenant.vue';
import AppHubBreadcrumb from '../hub/AppHubBreadcrumb.vue';
import AppHubSearch from '../hub/AppHubSearch.vue';
import AppHubStatus from '../hub/AppHubStatus.vue';
import AppThemeToggle from './AppThemeToggle.vue';
import AppProductSwitch from './AppProductSwitch.vue';
import AppMessages from './AppMessages.vue';

export default {
  name: 'AppSystemBar',
  components: { AppHubTenant, AppHubBreadcrumb, AppHubSearch, AppHubStatus, AppThemeToggle, AppProductSwitch, AppMessages },
  computed: {
    // Seitenrand wie AppPage: Touch unter 960 px 16 statt 20
    narrow() {
      const touch = !!(this.$dichte && this.$dichte.touch);
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 1280;
      return touch && width < 960;
    }
  }
};
</script>

<style scoped>
.sysbar {
  flex: none;
  height: var(--sysbar-h, 57px);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  border-bottom: 1px solid var(--line);
  background: var(--surface);
  color: var(--ink);
  font-family: var(--font);
  /* dieselbe feste Scrollrinne wie der Inhalt darunter (MainLayout), sonst stünden Suche und Status mit klassischen
     Scrollleisten rechts neben den Seitenaktionen */
  overflow: hidden;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
}
.sysbar--narrow {
  padding: 0 16px;
}
@media print {
  .sysbar {
    display: none;
  }
}

/* ---------------------------------------------------------------- links */
/* ohne Abstand: das Polster 8 des Mandanten trennt ihn vom ersten „/“ wie die Abstände in den Brotkrumen */
.sysbar__left {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 1 1 auto;
  min-width: 0;
}
/* Platzmangel wie in der Hub-Kopfleiste: der Mandant hat Vorrang (kürzt erst zuletzt), die Brotkrumen nehmen den Rest
   (erst entfällt die Gruppe, dann kürzt der Seitentitel). Mandant und Knopf haben innen 8 Polster: um 8 nach links
   versetzt, damit das Gebäude-Icon auf dem Seitenrand steht. */
.sysbar__tenant {
  flex: 0 0 auto;
  min-width: 0;
  max-width: calc(100% - 52px);
  margin-left: -8px;
}
.sysbar ::v-deep .htenant__name,
.sysbar ::v-deep .hcrumb__item--parent .hcrumb__text {
  color: var(--ink-3);
  font-weight: 400;
}
/* ohne Mandantennamen rendert AppHubTenant nichts: dann auch kein Versatz */
.sysbar__tenant:empty {
  display: none;
}
.sysbar__crumb {
  flex: 0 1 auto;
  min-width: 0;
}

/* ---------------------------------------------------------------- rechts */
.sysbar__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}
/* Suchpille und Status-Knopf wie die Knöpfe im Blatt (Kundenwunsch 12.10.2026): weiche Kante 1 px --edge mit feinem
   Schatten --sh-ctl, bei Hover, offen und gedrückt Kante --edge-hover (Flächen wie in den Bausteinen) */
.sysbar ::v-deep .hub-search,
.sysbar ::v-deep .hub-status {
  border: 1px solid var(--edge);
  box-shadow: var(--sh-ctl);
}
/* Tastenchip „⌘K“ als Pille, passend zur runden Suchpille (Kundenwunsch 12.10.2026) */
.sysbar ::v-deep .hub-search .hub-search__kbd {
  padding: 2px 7px;
  border-radius: 9999px;
}
:where(html:not(.dichte-touch)) .sysbar ::v-deep .hub-search:hover,
:where(html:not(.dichte-touch)) .sysbar ::v-deep .hub-status:hover,
.sysbar ::v-deep .hub-status[aria-expanded="true"],
.sysbar ::v-deep .hub-search:active,
.sysbar ::v-deep .hub-status:active {
  border-color: var(--edge-hover);
}
</style>
